'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

interface BlogTocProps {
  sections: TocSection[];
  topAnchorId?: string;
  offset?: number;
}
export interface TocSection {
  id: string;
  label: string;
}

interface UseActiveTocOptions {
  topAnchorId?: string;
  offset?: number;
}

/**
 * Shared table-of-contents logic: tracks the section matching the current scroll
 * position (scroll-spy) and exposes a `selectSection` that smooth-scrolls to a
 * section (or the article top for "introduction"). Used by both the desktop rail
 * and the mobile disclosure so they behave identically.
 */
function useActiveTocSection(
  sections: TocSection[],
  { topAnchorId = 'blog-content-container', offset = 140 }: UseActiveTocOptions,
) {
  const [activeId, setActiveId] = useState(sections[0]?.id ?? '');
  // Suppress scroll-spy briefly after a click so the smooth-scroll doesn't fight
  // the user's chosen section.
  const clickLockRef = useRef(false);
  const lockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (lockTimerRef.current) {
        clearTimeout(lockTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const headingSections = sections.filter((s) => s.id !== 'introduction');

    const computeActive = () => {
      if (clickLockRef.current) {
        return;
      }
      let current = sections[0]?.id ?? '';
      for (const section of headingSections) {
        const el = document.getElementById(section.id);
        if (el && el.getBoundingClientRect().top - offset <= 0) {
          current = section.id;
        }
      }
      setActiveId(current);
    };

    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(computeActive);
    };

    computeActive();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [sections, offset]);

  const selectSection = useCallback(
    (section: TocSection) => {
      const targetId = section.id === 'introduction' ? topAnchorId : section.id;
      document
        .getElementById(targetId)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveId(section.id);
      clickLockRef.current = true;
      if (lockTimerRef.current) {
        clearTimeout(lockTimerRef.current);
      }
      lockTimerRef.current = setTimeout(() => {
        clickLockRef.current = false;
      }, 1200);
    },
    [topAnchorId],
  );

  return { activeId, selectSection };
}

/** Presentational capsule list, shared by desktop + mobile. */
function TocList({
  sections,
  activeId,
  onSelect,
}: {
  sections: TocSection[];
  activeId: string;
  onSelect: (section: TocSection) => void;
}) {
  return (
    <nav aria-label="On this page" className="flex w-full flex-col gap-2">
      {sections.map((section) => {
        const active = activeId === section.id;
        return (
          <a
            key={section.id}
            href={section.id === 'introduction' ? '#' : `#${section.id}`}
            onClick={(e) => {
              e.preventDefault();
              onSelect(section);
            }}
            aria-current={active ? 'true' : undefined}
            className={`flex min-h-[50px] cursor-pointer items-center gap-[3px] rounded-[8px] px-[15px] py-2 transition-colors ${
              active ? 'bg-[#e0e0e0]' : 'bg-[#f4f4f4]'
            }`}
          >
            {active && (
              <span
                aria-hidden
                className="shrink-0 font-inter text-[12px] font-bold leading-[20px] text-[#4b0fea]"
              >
                →
              </span>
            )}
            <span
              className={
                active
                  ? 'font-inter text-[12px] font-bold leading-[20px] text-[#4b0fea]'
                  : 'font-inter text-[12px] font-medium leading-[20px] text-[#041118]'
              }
            >
              {section.label}
            </span>
          </a>
        );
      })}
    </nav>
  );
}

/**
 * Desktop table of contents — the sticky left rail of capsules. Positioning
 * (sticky/column) is handled by the parent grid cell.
 */
export function BlogToc({ sections, topAnchorId, offset }: BlogTocProps) {
  const { activeId, selectSection } = useActiveTocSection(sections, {
    topAnchorId,
    offset,
  });
  return (
    <TocList sections={sections} activeId={activeId} onSelect={selectSection} />
  );
}

/**
 * Mobile/tablet table of contents — a collapsible "On this page" disclosure.
 * The collapsed bar shows the current section (scroll-spy); tapping it opens a
 * dropdown with the full list; selecting an item scrolls and closes it.
 * Meant to be wrapped in a sticky container by the parent.
 */
export function BlogTocMobile({ sections, topAnchorId, offset }: BlogTocProps) {
  const { activeId, selectSection } = useActiveTocSection(sections, {
    topAnchorId,
    offset,
  });
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onDocMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  // Nothing useful to navigate with a single (or no) section.
  if (sections.length < 2) {
    return null;
  }

  const activeLabel =
    sections.find((s) => s.id === activeId)?.label ?? sections[0]?.label ?? '';

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 rounded-[8px] bg-[#f4f4f4] px-[15px] py-3 text-left"
      >
        <span className="flex min-w-0 flex-col">
          <span className="font-inter text-[11px] font-medium uppercase leading-[16px] tracking-wide text-[#041118]/60">
            On this page
          </span>
          <span className="truncate font-inter text-[14px] font-bold leading-[20px] text-[#041118]">
            {activeLabel}
          </span>
        </span>
        <svg
          className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="#041118"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute inset-x-0 top-full z-30 mt-2 max-h-[60vh] overflow-auto rounded-[8px] border border-[#e0e0e0] bg-white p-2 shadow-[0_12px_24px_rgba(0,0,0,0.12)]">
          <TocList
            sections={sections}
            activeId={activeId}
            onSelect={(section) => {
              selectSection(section);
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}
